import { useState, useRef, useEffect } from 'react';
import logger from './../utils/logger.js';


// Global detector instance to ensure we only initialize once
let globalDetectorInstance = null;
let isDetectorInitializing = false;
let detectorInitPromise = null;

/**
 * A shared hook for face detection that can be reused across components
 * This ensures the face detector is only initialized once in the application
 */
const useFaceDetection = (videoRef, canvasRef) => {
  const [faceCount, setFaceCount] = useState(0);
  const [faceDetectionStatus, setFaceDetectionStatus] = useState("");
  const [isDetectorReady, setIsDetectorReady] = useState(!!globalDetectorInstance);
  const [detections, setDetections] = useState([]);
  
  const animationRef = useRef(null);
  const lastForceUpdateTime = useRef(0);
  
  // Draw face detection results on canvas
  const drawDetections = (detections) => {
    if (!canvasRef?.current || !videoRef?.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Set canvas dimensions to match video display size (not intrinsic size)
    const videoRect = video.getBoundingClientRect();
    canvas.width = videoRect.width;
    canvas.height = videoRect.height;
    
    // Calculate scaling factors between video intrinsic size and display size
    const scaleX = canvas.width / video.videoWidth || 1;
    const scaleY = canvas.height / video.videoHeight || 1;
    
    // Clear previous drawings
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw each detection
    detections.forEach(detection => {
      try {
        // Scale detection coordinates to match canvas size
        const x = detection.boundingBox.originX * scaleX;
        const y = detection.boundingBox.originY * scaleY;
        const w = detection.boundingBox.width * scaleX;
        const h = detection.boundingBox.height * scaleY;
        
        // Draw bounding box
        ctx.strokeStyle = detections.length > 1 ? 'red' : '#00ff00';
        ctx.lineWidth = 3;
        ctx.strokeRect(x, y, w, h);
        
        // Draw score text
        // ctx.fillStyle = "white";
        // ctx.strokeStyle = "black";
        // ctx.lineWidth = 1;
        // ctx.font = `${Math.max(16, Math.round(canvas.width / 30))}px Arial`;
        // const score = Math.round(detection.categories[0].score * 100);
        // const text = `${score}%`;
        
        // // Position text above the box
        // const textWidth = ctx.measureText(text).width;
        // const textX = x + (w - textWidth) / 2;
        // const textY = y > 20 ? y - 10 : y + h + 20;
        
        // ctx.fillText(text, textX, textY);
        // ctx.strokeText(text, textX, textY);
        
        // Draw keypoints if available
        if (detection.keypoints) {
          detection.keypoints.forEach(keypoint => {
            const kpX = keypoint.x * video.videoWidth * scaleX;
            const kpY = keypoint.y * video.videoHeight * scaleY;
            
            ctx.fillStyle = "cyan";
            ctx.beginPath();
            ctx.arc(kpX, kpY, 4, 0, 2 * Math.PI);
            ctx.fill();
          });
        }
      } catch (error) {
        logger.error("Error drawing detection:", error);
      }
    });
  };
  
  // Initialize face detector (only once globally)
  const initializeDetector = async () => {
    // If detector is already initialized, return it
    if (globalDetectorInstance) {
      return globalDetectorInstance;
    }
    
    // If detector is being initialized, wait for it
    if (isDetectorInitializing && detectorInitPromise) {
      return detectorInitPromise;
    }
    
    // Start initialization
    isDetectorInitializing = true;
    
    detectorInitPromise = new Promise(async (resolve, reject) => {
      try {
        logger.debug("Initializing global face detector...");
        
        // Dynamically import MediaPipe
        const { FaceDetector, FilesetResolver } = await import(
          "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.0"
        );
        
        // Load vision wasm files
        const vision = await FilesetResolver.forVisionTasks(
          "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.0/wasm"
        );
        
        // Create face detector
        const detector = await FaceDetector.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath: "https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/1/blaze_face_short_range.tflite",
            delegate: "GPU"
          },
          runningMode: "VIDEO"
        });
        
        // Store globally
        globalDetectorInstance = detector;
        logger.debug("Global face detector initialized successfully");
        
        resolve(detector);
      } catch (error) {
        logger.error("Error initializing face detector:", error);
        isDetectorInitializing = false;
        detectorInitPromise = null;
        reject(error);
      }
    });
    
    try {
      const detector = await detectorInitPromise;
      isDetectorInitializing = false;
      return detector;
    } catch (error) {
      return null;
    }
  };
  
  // Initialize detector on mount
  useEffect(() => {
    let isMounted = true;
    
    const setupDetector = async () => {
      try {
        logger.debug("Setting up face detector...");
        
        // Add a timeout check to prevent infinite loading on problematic devices
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error("Face detector initialization timeout")), 10000);
        });
        
        // Race between detector initialization and timeout
        let detector;
        try {
          detector = await Promise.race([
            initializeDetector(),
            timeoutPromise
          ]);
        } catch (timeoutError) {
          logger.warn("Face detector initialization took too long:", timeoutError);
          // Proceed without face detection rather than blocking the flow
          if (isMounted) {
            setFaceDetectionStatus("face_detected"); // Force-enable progression
            setFaceCount(1); // Simulate a face detection to allow proceeding
            setIsDetectorReady(true);
          }
          return;
        }
        
        if (isMounted && detector) {
          logger.debug("Face detector ready!");
          setIsDetectorReady(true);
        }
      } catch (error) {
        logger.error("Error setting up face detector:", error);
        if (isMounted) {
          // On error, also simulate face detection to let users proceed
          setFaceDetectionStatus("face_detected");
          setFaceCount(1);
          setIsDetectorReady(true);
        }
      }
    };
    
    if (!globalDetectorInstance) {
      setupDetector();
    } else {
      logger.debug("Using existing face detector instance");
      setIsDetectorReady(true);
    }
    
    return () => {
      isMounted = false;
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);
  
  // Start detection when video is available
  useEffect(() => {
    let lastVideoTime = -1;
    let detectionStarted = false;
    
    const runDetection = () => {
      const video = videoRef?.current;
      
      if (!video || !globalDetectorInstance || !isDetectorReady) {
        animationRef.current = requestAnimationFrame(runDetection);
        return;
      }
      
      // Check if video is ready for detection
      if (!detectionStarted && (video.readyState >= 2)) {
        logger.debug("Video ready for detection, starting detection loop");
        detectionStarted = true;
      }
      
      if (video.paused || video.ended) {
        animationRef.current = requestAnimationFrame(runDetection);
        return;
      }
      
      // Force update every 100ms regardless of video time changes
      // This ensures detection runs even when video.currentTime doesn't update
      const now = Date.now();
      const timeChanged = video.currentTime !== lastVideoTime;
      const forceUpdate = (now - lastForceUpdateTime.current) > 100;
      
      if (timeChanged || forceUpdate) {
        lastVideoTime = video.currentTime;
        if (forceUpdate) {
          lastForceUpdateTime.current = now;
        }
        
        try {
          const startTimeMs = performance.now();
          const results = globalDetectorInstance.detectForVideo(video, startTimeMs);
          
          if (results && results.detections) {
            setDetections(results.detections);
            setFaceCount(results.detections.length);
            
            // Update status message based on face count
            if (results.detections.length === 0) {
              setFaceDetectionStatus("no_face");
            } else if (results.detections.length > 1) {
              setFaceDetectionStatus("multiple_faces");
            } else {
              if (faceDetectionStatus !== "face_detected") {
                setFaceDetectionStatus("face_detected");
                // logger.debug("Face detected!");
              }
            }
            
            // Draw detections on canvas if canvas ref is provided
            if (canvasRef?.current) {
              drawDetections(results.detections);
            }
          }
        } catch (error) {
          logger.error("Face detection error:", error);
        }
      }
      
      animationRef.current = requestAnimationFrame(runDetection);
    };
    
    if (isDetectorReady && videoRef?.current) {
      logger.debug("Starting face detection loop...");
      
      // Initialize canvas size if canvas ref is provided
      if (canvasRef?.current) {
        const videoRect = videoRef.current.getBoundingClientRect();
        canvasRef.current.width = videoRect.width;
        canvasRef.current.height = videoRect.height;
      }
      
      runDetection();
    }
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isDetectorReady, videoRef, canvasRef]);
  
  return { 
    faceCount, 
    faceDetectionStatus, 
    isDetectorReady, 
    detections,
    // Export the draw function in case it's needed elsewhere
    drawDetections: canvasRef ? drawDetections : null
  };
};

// Utility function to cleanup the global detector instance
// Call this when you want to force re-initialization
useFaceDetection.cleanup = () => {
  globalDetectorInstance = null;
  isDetectorInitializing = false;
  detectorInitPromise = null;
  logger.debug("Global face detector instance cleaned up");
};

export default useFaceDetection;
